import { Link } from "react-router-dom";
import { Truck, MapPin, Clock, Shield, ChevronRight, CheckCircle, Package, Route } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

const HomePage = () => {
  const services = [
    {
      icon: <Truck className="h-8 w-8" />,
      title: "Dry Van Freight",
      description: "Dry van trucks for all types of general merchandise, pallets, and dry goods transportation.",
    },
    {
      icon: <Route className="h-8 w-8" />,
      title: "Nationwide Routes",
      description: "Coverage across the entire United States with competitive delivery times and real-time tracking.",
    },
    {
      icon: <Package className="h-8 w-8" />,
      title: "Palletized Cargo",
      description: "Specialists in palletized freight transport with careful handling and specialized equipment.",
    },
  ];

  const benefits = [
    "Modern, well-maintained fleet",
    "Professional certified drivers",
    "Real-time GPS tracking",
    "Full cargo insurance",
    "Competitive pricing",
    "24/7 service",
  ];

  const stats = [
    { value: "500+", label: "Monthly Deliveries" },
    { value: "48", label: "States Covered" },
    { value: "15+", label: "Years Experience" },
    { value: "98%", label: "Customer Satisfaction" },
  ];

  return (
    <div data-testid="home-page">
      <SEOHead 
        title="KDA Trucking LLC - Dry Van Freight Transportation | MA, NH, ME, RI"
        description="Professional dry van trucking company serving Massachusetts, New Hampshire, Maine, and Rhode Island. Reliable freight transportation nationwide. Call (617) 913-2934 for a free quote!"
      />
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1694090709409-78b1175a54a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxlbmNsb3NlZCUyMHRyYWlsZXIlMjBzZW1pJTIwdHJ1Y2slMjB3aGl0ZXxlbnwwfHx8fDE3NzExNjgyMjR8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="section-container relative z-10">
          <div className="max-w-3xl animate-slide-up">
            <p className="text-orange-500 font-bold uppercase tracking-widest mb-4 text-sm">
              Reliable Freight Transportation
            </p>
            <h1 className="heading-1 text-white mb-6">
              We Move Your Cargo<br />
              <span className="text-orange-500">With Confidence</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl mb-8 max-w-xl leading-relaxed">
              Professional dry van freight transportation service across the United States. 
              Safe, on-time deliveries at competitive prices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/request-service" data-testid="hero-cta-primary">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-base shadow-lg hover:shadow-orange-500/30 transition-all duration-200 hover:-translate-y-1">
                  Request a Quote
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-cta-secondary">
                <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-base transition-all duration-200">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-slate-900 py-12" data-testid="stats-section">
        <div className="section-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-orange-500 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {stat.value}
                </p>
                <p className="text-slate-400 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-spacing bg-slate-50" data-testid="services-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <p className="text-orange-600 font-bold uppercase tracking-widest mb-4 text-sm">
              Our Services
            </p>
            <h2 className="heading-2 text-slate-900">
              Transportation Solutions
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="card-service group"
                data-testid={`service-card-${index}`}
              >
                <div className="text-orange-600 mb-6 group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="heading-3 text-slate-900 mb-4">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-spacing" data-testid="about-section">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-orange-600 font-bold uppercase tracking-widest mb-4 text-sm">
                About Us
              </p>
              <h2 className="heading-2 text-slate-900 mb-6">
                Your Logistics Partner
              </h2>
              <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                KDA Trucking LLC is a freight transportation company committed to service 
                excellence. With over 15 years of experience, we offer reliable and efficient 
                transportation solutions for businesses of all sizes.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <Link to="/request-service" data-testid="about-cta">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold uppercase tracking-wider px-8 py-4 transition-all duration-200 hover:-translate-y-1">
                  Get Started
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1601467995997-ac1ae9a8fff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxlbmNsb3NlZCUyMHRyYWlsZXIlMjBzZW1pJTIwdHJ1Y2slMjB3aGl0ZXxlbnwwfHx8fDE3NzExNjgyMjR8MA&ixlib=rb-4.1.0&q=85"
                alt="Dry van truck"
                className="rounded-sm shadow-2xl w-full"
                data-testid="about-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-orange-600 text-white p-6 rounded-sm shadow-xl hidden md:block">
                <p className="text-4xl font-bold mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>15+</p>
                <p className="text-sm uppercase tracking-wider">Years of Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 bg-white" data-testid="service-areas-section">
        <div className="section-container">
          <div className="text-center mb-12">
            <p className="text-orange-600 font-bold uppercase tracking-widest mb-4 text-sm">
              Service Areas
            </p>
            <h2 className="heading-2 text-slate-900">
              Serving New England & Beyond
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Link 
              to="/trucking/massachusetts" 
              className="bg-slate-50 p-6 border-l-4 border-orange-500 hover:bg-white transition-colors group"
              data-testid="service-area-ma"
            >
              <h3 className="font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">Massachusetts</h3>
              <p className="text-slate-600 text-sm">Boston, Worcester & more</p>
            </Link>
            <Link 
              to="/trucking/new-hampshire" 
              className="bg-slate-50 p-6 border-l-4 border-orange-500 hover:bg-white transition-colors group"
              data-testid="service-area-nh"
            >
              <h3 className="font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">New Hampshire</h3>
              <p className="text-slate-600 text-sm">Manchester, Nashua & more</p>
            </Link>
            <Link 
              to="/trucking/maine" 
              className="bg-slate-50 p-6 border-l-4 border-orange-500 hover:bg-white transition-colors group"
              data-testid="service-area-me"
            >
              <h3 className="font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">Maine</h3>
              <p className="text-slate-600 text-sm">Portland, Bangor & more</p>
            </Link>
            <Link 
              to="/trucking/rhode-island" 
              className="bg-slate-50 p-6 border-l-4 border-orange-500 hover:bg-white transition-colors group"
              data-testid="service-area-ri"
            >
              <h3 className="font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">Rhode Island</h3>
              <p className="text-slate-600 text-sm">Providence, Warwick & more</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-20" data-testid="cta-section">
        <div className="section-container text-center">
          <h2 className="heading-2 text-white mb-6">
            Ready to Move Your Cargo?
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Request a free quote today and discover why we are the preferred choice 
            of hundreds of companies across the country.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-service" data-testid="cta-primary">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-base shadow-lg hover:shadow-orange-500/30 transition-all duration-200 hover:-translate-y-1">
                Request a Quote
              </Button>
            </Link>
            <a href="tel:+16179132934" data-testid="cta-call">
              <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-base transition-all duration-200">
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
